import { SADropdownOption } from '@missionlabs/smartagent-app-components/dist/Dropdown';
import { IContactAudit } from '@missionlabs/smartagent-service-contact/dist/lib/entities/contact-audit';
import { IAutomatedActionAudit } from '@missionlabs/smartagent-service-thread/dist/entities/action-audit';
import { AllThreadsAndMessages } from '@missionlabs/smartagent-service-thread';
import { ThunkDispatch } from 'redux-thunk';

import { Agent } from 'components/Dropdowns/agents';
import { Queue } from 'components/Dropdowns/queues';

import * as ContactAPI from 'services/api/api.contact';

import RootState from 'store/state';
import ContactState, { ICallContactAttributes } from 'store/contact/contact.state';

import { IInteraction } from 'views/Metrics/HistoricMetrics/types';

import {
    ContactChannelDropdown,
    ContactInitiationMethod,
    ContactSearchActionTypes,
    IContactSearchForm,
} from './contactSearch.types';
import { getChannelGroup } from './contactSearch.utils';

import { IEmailAttachment } from 'components/EmailMessage/types';
import { getDownloadURL } from 'services/api/api.thread';
import { getActionAudit } from 'services/api/api.automated-rules';
import { transformTaskContacts, transformContacts, getThreadMessageGroups, transformSMSCampaignETRToInteraction } from 'views/Metrics/HistoricMetrics/utils';
import { IETR } from '@missionlabs/smartagent-lib-shared';

interface IParams {
    [key: string]: string;
}

export type ContactSearchAction =
    | { type: ContactSearchActionTypes.LOADING_INTERACTIONS; payload: boolean }
    | {
          type: ContactSearchActionTypes.RESET_FORM_PARAMS;
          payload: IContactSearchForm;
      }
    | {
          type: ContactSearchActionTypes.SET_FORM_PARAMS;
          payload: IContactSearchForm;
      }
    | { type: ContactSearchActionTypes.SET_AGENT; payload: Agent | undefined }
    | { type: ContactSearchActionTypes.SET_CALL_DURATION_END_TIME; payload: string }
    | { type: ContactSearchActionTypes.SET_CALL_DURATION_START_TIME; payload: string }
    | {
          type: ContactSearchActionTypes.SET_CHANNEL;
          payload: ContactChannelDropdown;
      }
    | {
          type: ContactSearchActionTypes.SET_CHAT_EMAIL;
          payload: string;
      }
    | {
          type: ContactSearchActionTypes.SET_CHAT_ORDER_NUMBER;
          payload: string;
      }
    | { type: ContactSearchActionTypes.SET_CONTACT_ID; payload: string }
    | { type: ContactSearchActionTypes.SET_APP_CONTACT_ID; payload: string | undefined }
    | { type: ContactSearchActionTypes.SET_SELECTED_INTERACTION; payload: IInteraction | undefined }
    | { type: ContactSearchActionTypes.SET_INTERACTIONS; payload: IInteraction[] }
    | { type: ContactSearchActionTypes.SET_CUSTOMER_PHONE; payload: string }
    | { type: ContactSearchActionTypes.SET_EMAIL; payload: string }
    | { type: ContactSearchActionTypes.SET_END_DATE; payload: Date }
    | { type: ContactSearchActionTypes.SET_END_TIME; payload: string }
    | { type: ContactSearchActionTypes.SET_EXTERNAL_ID; payload: string }
    | { type: ContactSearchActionTypes.SET_HOLD_DURATION_END_TIME; payload: string }
    | { type: ContactSearchActionTypes.SET_HOLD_DURATION_START_TIME; payload: string }
    | {
          type: ContactSearchActionTypes.SET_INITIATION_METHOD;
          payload: SADropdownOption<ContactInitiationMethod | undefined> | undefined;
      }
    | {
          type: ContactSearchActionTypes.SET_KEYWORD;
          payload: string;
      }
    | {
          type: ContactSearchActionTypes.SET_SOCIAL_HANDLE;
          payload: string;
      }
    | { type: ContactSearchActionTypes.SET_START_DATE; payload: Date }
    | { type: ContactSearchActionTypes.SET_START_TIME; payload: string }
    | { type: ContactSearchActionTypes.SET_QUEUE; payload: Queue }
    | {
          type: ContactSearchActionTypes.SET_UNIQUE_SOCIAL_ID;
          payload: string;
      }
    | {
          type: ContactSearchActionTypes.SET_QUEUED_TASKS_AUDIT;
          payload: { interactionID: string; queuedTasksAudit: IContactAudit[] };
      }
    | {
          type: ContactSearchActionTypes.SET_AUTOMATED_RULES_AUDIT;
          payload: { interactionID: string; automatedRulesAudit: IAutomatedActionAudit[] };
      }
    | {
          type: ContactSearchActionTypes.ADD_TRANSFORMED_ATTACHMENTS;
          payload: { interactionID: string; transformedAttachments: IEmailAttachment[] };
      }
    | {
          type: ContactSearchActionTypes.SET_INTERACTION_ATTRIBUTES;
          payload: { interactionID: string; attributes: ICallContactAttributes };
      }
    | {
          type: ContactSearchActionTypes.SET_EMAIL;
          payload: string;
      }
    | {
          type: ContactSearchActionTypes.SET_EMAIL_SEARCH_TYPE;
          payload: SADropdownOption<string>;
      };

export const loadingInteractions = (loading = true): ContactSearchAction => {
    return {
        type: ContactSearchActionTypes.LOADING_INTERACTIONS,
        payload: loading,
    };
};

export const setAgent = (agent?: Agent): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_AGENT, payload: agent };
};

export const setCallDurationEndTime = (callDurationEndTime: string): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_CALL_DURATION_END_TIME, payload: callDurationEndTime };
};

export const setCallDurationStartTime = (callDurationStartTime: string): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_CALL_DURATION_START_TIME, payload: callDurationStartTime };
};

export const setChannel = (channel: SADropdownOption<any>): ContactSearchAction => {
    return {
        type: ContactSearchActionTypes.SET_CHANNEL,
        payload: { ...channel, group: getChannelGroup(channel.data) },
    };
};

export const setChatEmail = (chatEmail: string): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_CHAT_EMAIL, payload: chatEmail };
};

export const setChatOrderNumber = (chatOrderNumber: string): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_CHAT_ORDER_NUMBER, payload: chatOrderNumber };
};

export const setContactID = (contactID: string): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_CONTACT_ID, payload: contactID };
};

export const setInteractions = (interactions: IInteraction[]): ContactSearchAction => {
    return {
        type: ContactSearchActionTypes.SET_INTERACTIONS,
        payload: interactions,
    };
};

export const setAppContactId = (ID: string | undefined): ContactSearchAction => {
    return {
        type: ContactSearchActionTypes.SET_APP_CONTACT_ID,
        payload: ID,
    };
};

export const setSelectedInteraction = (interaction: IInteraction | undefined): ContactSearchAction => {
    return {
        type: ContactSearchActionTypes.SET_SELECTED_INTERACTION,
        payload: interaction,
    };
};

export const setFormParams = (form: IContactSearchForm): ContactSearchAction => {
    return {
        type: ContactSearchActionTypes.SET_FORM_PARAMS,
        payload: form,
    };
};

export const setKeyword = (keyword: string): ContactSearchAction => {
    return {
        type: ContactSearchActionTypes.SET_KEYWORD,
        payload: keyword,
    };
};

export const setCustomerPhone = (customerPhoneNumber: string): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_CUSTOMER_PHONE, payload: customerPhoneNumber };
};

export const setEndDate = (endDate: Date): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_END_DATE, payload: endDate };
};

export const setEndTime = (endTime: string): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_END_TIME, payload: endTime };
};

export const setExternalID = (externalID: string): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_EXTERNAL_ID, payload: externalID };
};

export const setHoldDurationEndTime = (holdDurationEndTime: string): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_HOLD_DURATION_END_TIME, payload: holdDurationEndTime };
};

export const setHoldDurationStartTime = (holdDurationStartTime: string): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_HOLD_DURATION_START_TIME, payload: holdDurationStartTime };
};

export const setInitiationMethod = (
    initiationMethod: SADropdownOption<ContactInitiationMethod | undefined> | undefined
): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_INITIATION_METHOD, payload: initiationMethod };
};

export const resetFormParams = (contactSearchForm: IContactSearchForm): ContactSearchAction => {
    return { type: ContactSearchActionTypes.RESET_FORM_PARAMS, payload: contactSearchForm };
};

export const setSocialHandle = (socialHandle: string): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_SOCIAL_HANDLE, payload: socialHandle };
};

export const setEmail = (email: string): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_EMAIL, payload: email };
};

export const setEmailSearchType = (email: SADropdownOption<string>): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_EMAIL_SEARCH_TYPE, payload: email };
};

export const setStartDate = (startDate: Date): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_START_DATE, payload: startDate };
};

export const setStartTime = (startTime: string): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_START_TIME, payload: startTime };
};

export const setQueue = (queue: Queue): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_QUEUE, payload: queue };
};

export const setUniqueSocialID = (uniqueSocialID: string): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_UNIQUE_SOCIAL_ID, payload: uniqueSocialID };
};

export const setQueuedTasksAudit = (interactionID: string, queuedTasksAudit: IContactAudit[]): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_QUEUED_TASKS_AUDIT, payload: { interactionID, queuedTasksAudit } };
};

export const setInteractionAttributes = (
    interactionID: string,
    attributes: ICallContactAttributes
): ContactSearchAction => {
    return { type: ContactSearchActionTypes.SET_INTERACTION_ATTRIBUTES, payload: { interactionID, attributes } };
};

export const setAutomatedRulesAudit = (
    interactionID: string,
    automatedRulesAudit: IAutomatedActionAudit[]
): ContactSearchAction => {
    return {
        type: ContactSearchActionTypes.SET_AUTOMATED_RULES_AUDIT,
        payload: { interactionID, automatedRulesAudit },
    };
};

export function addTransformedAttachments(
    interactionID: string,
    transformedAttachments: IEmailAttachment[]
): ContactSearchAction {
    return {
        type: ContactSearchActionTypes.ADD_TRANSFORMED_ATTACHMENTS,
        payload: { interactionID, transformedAttachments },
    };
}

export const fetchInteractions = (queryString: string) => {
    return async (dispatch: ThunkDispatch<RootState, undefined, ContactSearchAction>, getState: () => RootState) => {
        try {
            const { app, auth } = getState();

            const parseQuerystring = (qs: string) => {
                return qs.split('&').reduce((acc, val) => {
                    return { ...acc, [val.split('=')[0]]: val.split('=')[1] };
                }, {});
            };

            const { ID: companyID, instance: { ID: instanceID } = {} } = app;
            const { token } = auth;

            if (!instanceID) throw new Error('No instance ID found');
            if (!token) throw new Error('No token found');

            dispatch(loadingInteractions());

            const contacts = (await ContactAPI.getContacts(companyID, instanceID, token, queryString)).data;

            const taskContacts: ContactState[] = [];
            const otherContacts: ContactState[] = [];

            // Split contacts into two arrays with one array containing task contacts and the other array containing the remaining contacts
            contacts.forEach(contact =>
                contact.channel === 'TASK' ? taskContacts.push(contact) : otherContacts.push(contact)
            );

            const params: IParams = parseQuerystring(queryString);
            
            const eventTraceRecords: IETR[] = [];
            if (params['channel'] === 'SMS+DM') {
                eventTraceRecords.push(...await ContactAPI.getEventTraceRecords(queryString))
            }
            const etrInteractions = eventTraceRecords.map(transformSMSCampaignETRToInteraction) as IInteraction[]

            const threadMessageGroups = await getThreadMessageGroups(taskContacts);

            // If this is a keyword search then provide the keyword so that we can filter the threads as well as the taskContacts
            const filteredThreadMessageGroups = params['keyword']
                ? (threadMessageGroups.map(thread => {
                      return {
                          ...thread,
                          messages: thread.messages.filter(message => {
                              return message.content.toLowerCase().includes(params['keyword']) ||
                                  message.subject?.toLowerCase().includes(params['keyword']);
                          }),
                      };
                  }) as AllThreadsAndMessages[])
                : [...threadMessageGroups];

            const taskInteractions = transformTaskContacts(taskContacts, filteredThreadMessageGroups);
            const otherInteractions = transformContacts(otherContacts);
            const allInteractions = taskInteractions.concat(otherInteractions, etrInteractions);
            dispatch(setInteractions(allInteractions));
        } catch (error) {
            console.error('Error fetching interactions: ', error);

            dispatch(setInteractions([]));
        }
    };
};

export function fetchAutomatedRulesAudit(interactionID: string, actionAuditID: string | undefined) {
    return async (dispatch: ThunkDispatch<RootState, void, ContactSearchAction>, getState: () => RootState) => {
        if (!actionAuditID) {
            dispatch(setAutomatedRulesAudit(interactionID, []));
            return;
        }

        try {
            const { app, auth } = getState();

            const { ID: companyID, instance: { ID: instanceID } = {} } = app;
            const { token } = auth;

            if (!companyID) throw new Error('No company ID found');
            if (!instanceID) throw new Error('No instance ID found');
            if (!token) throw new Error('No token found');

            const response = await getActionAudit(companyID, instanceID, token, actionAuditID);

            dispatch(setAutomatedRulesAudit(interactionID, response.data));
        } catch (error) {
            console.error('Error fetching automated rules audit data: ', error);
            dispatch(setAutomatedRulesAudit(interactionID, []));
        }
    };
}

export function fetchQueuedTasksAudit(interactionID: string) {
    return async (dispatch: ThunkDispatch<RootState, void, ContactSearchAction>, getState: () => RootState) => {
        try {
            const { app, auth } = getState();

            const { ID: companyID, instance: { ID: instanceID } = {} } = app;
            const { token } = auth;

            if (!companyID) throw new Error('No company ID found');
            if (!instanceID) throw new Error('No instance ID found');
            if (!token) throw new Error('No token found');

            const response = await ContactAPI.getContactAudit(companyID, instanceID, token, interactionID);

            dispatch(setQueuedTasksAudit(interactionID, response.data));
        } catch (error) {
            console.error('Error fetching queued tasks audit data: ', error);
            dispatch(setQueuedTasksAudit(interactionID, []));
        }
    };
}

export function fetchAttachmentUrls(attachments: IEmailAttachment[], messageId: string, threadId: string) {
    return async (dispatch: ThunkDispatch<RootState, void, ContactSearchAction>, getState: () => RootState) => {
        try {
            const { app, auth } = getState();

            const { ID: instanceID } = app.instance ?? {};
            const { token } = auth;

            if (!instanceID) throw new Error('No instance ID found');
            if (!token) throw new Error('No token found');

            const newAttachments: IEmailAttachment[] = await Promise.all(
                attachments.map(async attachment => {
                    const { s3Key, filename } = attachment;
                    let url = '';
                    try {
                        url = (await getDownloadURL(threadId, messageId, encodeURI(s3Key))).url;
                    } catch (error) {
                        console.error(`Error retrieving URL for ${s3Key}/${filename}: ${error}`);
                    }

                    return { ...attachment, s3URL: url };
                })
            );
            dispatch(addTransformedAttachments(messageId, newAttachments));
        } catch (error) {
            console.error('Error fetching attachment S3 urls:', error);

            dispatch(addTransformedAttachments(messageId, attachments));
        }
    };
}

export const fetchInteractionAttributes = (interaction: IInteraction) => {
    return async (dispatch: ThunkDispatch<RootState, void, ContactSearchAction>, getState: () => RootState) => {
        const { ID: interactionID, attributes: originalAttributes } = interaction;

        try {
            const { attributes } = await ContactAPI.getContact(interactionID);

            dispatch(setInteractionAttributes(interactionID, attributes));
        } catch (error) {
            console.error('Error fetching interaction attributes:', error);

            dispatch(setInteractionAttributes(interactionID, originalAttributes));
        }
    };
};
